import React, { useState, useEffect } from "react";
import {
  EyeOff, Layout, ShieldCheck, Check,
  Monitor, AlertCircle, Save, Timer, Keyboard
} from "lucide-react";
import { toast } from "react-toastify";
import api from "../utils/api";
import { globalAppCache } from "../utils/cache";

const Toggle = ({ enabled, onChange, id, disabled = false }) => (
  <button
    id={id}
    type="button"
    disabled={disabled}
    onClick={() => onChange(!enabled)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
      disabled ? "opacity-50 cursor-not-allowed" : ""
    } ${enabled ? "bg-teal-600" : "bg-slate-300"}`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
        enabled ? "translate-x-5" : "translate-x-0"
      }`}
    />
  </button>
);

export default function PosDisplaySettings({ userId, settings, onUpdate }) {
  const [posConfig, setPosConfig] = useState({
    showQuickStatsStrip: settings?.posSettings?.showQuickStatsStrip ?? true,
    statsVisibility: settings?.posSettings?.statsVisibility || "full", // "full" | "actions_only" | "hidden"
    maskSalesByDefault: Boolean(settings?.posSettings?.maskSalesByDefault ?? false),
    restrictStatsToManager: Boolean(settings?.posSettings?.restrictStatsToManager ?? false),
    autoMaskTimeout: Number(settings?.posSettings?.autoMaskTimeout ?? 45),
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings?.posSettings) {
      setPosConfig({
        showQuickStatsStrip: settings.posSettings.showQuickStatsStrip ?? true,
        statsVisibility: settings.posSettings.statsVisibility || "full",
        maskSalesByDefault: Boolean(settings.posSettings.maskSalesByDefault ?? false),
        restrictStatsToManager: Boolean(settings.posSettings.restrictStatsToManager ?? false),
        autoMaskTimeout: Number(settings.posSettings.autoMaskTimeout ?? 45),
      });
    }
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await api.put(
        `user/${userId}`,
        { posSettings: posConfig },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update memory cache
      if (globalAppCache.userCache[userId]) {
        globalAppCache.userCache[userId].posSettings = posConfig;
      }

      // Notify any active listeners across components
      window.dispatchEvent(
        new CustomEvent("posSettingsUpdated", { detail: posConfig })
      );

      toast.success("POS display settings saved successfully! ✓");
      if (onUpdate) onUpdate({ posSettings: posConfig });
    } catch (err) {
      console.error("Failed to update POS settings:", err);
      toast.error(err.response?.data?.msg || "Failed to save POS settings");
    } finally {
      setSaving(false);
    }
  };

  const isStripActive = posConfig.showQuickStatsStrip && posConfig.statsVisibility !== "hidden";

  return (
    <div
      className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200/70 shadow-sm space-y-6"
      data-aos="fade-up"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-5">
        <div className="flex items-center gap-3.5">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-tr from-slate-900 to-gray-800 flex items-center justify-center text-teal-400 shadow-md">
            <Monitor className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-slate-800 font-serif">
                POS Display & Countertop Privacy
              </h2>
              <span
                className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full ${
                  isStripActive
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : "bg-amber-50 text-amber-700 border border-amber-200"
                }`}
              >
                {isStripActive ? "Strip Active" : "Strip Hidden"}
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Control the top Today's sales bar visibility, protect financial data from customers, and restrict staff view.
            </p>
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-teal-600 hover:bg-teal-700 active:scale-[0.98] text-white rounded-xl text-xs font-bold transition shadow-sm disabled:opacity-50 shrink-0"
        >
          {saving ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          <span>{saving ? "Saving..." : "Save POS Settings"}</span>
        </button>
      </div>

      {/* Main Master Switch: Enable / Disable Today's Strip */}
      <div className="bg-slate-50/80 border border-slate-200/60 rounded-2xl p-4 sm:p-5 flex items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Layout className="w-4 h-4 text-teal-600" />
            <span className="text-sm font-bold text-slate-800">
              Show Today's Sales Strip on POS
            </span>
          </div>
          <p className="text-xs text-slate-500 max-w-xl">
            Displays the top dark bar on POS and table selection screens. If removed from POS by staff or cashier, you can re-enable it here anytime.
          </p>
        </div>
        <Toggle
          id="toggle-pos-strip"
          enabled={posConfig.showQuickStatsStrip}
          onChange={(val) =>
            setPosConfig((prev) => ({
              ...prev,
              showQuickStatsStrip: val,
              statsVisibility: val && prev.statsVisibility === "hidden" ? "full" : prev.statsVisibility,
            }))
          }
        />
      </div>

      {/* Display Modes Selector */}
      {posConfig.showQuickStatsStrip && (
        <div className="space-y-3">
          <label className="text-xs font-black uppercase tracking-wider text-slate-400 block">
            Strip Display Mode
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            {/* Mode 1: Full */}
            <div
              onClick={() =>
                setPosConfig((prev) => ({ ...prev, statsVisibility: "full" }))
              }
              className={`p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                posConfig.statsVisibility === "full"
                  ? "border-teal-600 bg-teal-50/40 shadow-sm"
                  : "border-slate-200 hover:border-slate-300 bg-white"
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center font-bold text-xs">
                    ₹
                  </div>
                  <span className="text-sm font-bold text-slate-800">
                    Full (Sales Data + Shortcuts)
                  </span>
                </div>
                {posConfig.statsVisibility === "full" && (
                  <Check className="w-4 h-4 text-teal-600" />
                )}
              </div>
              <p className="text-xs text-slate-500 mt-2 pl-9">
                Shows today's Revenue, Orders count, Average Ticket, and Top Item, along with all quick action shortcuts.
              </p>
            </div>

            {/* Mode 2: Shortcuts Only (Privacy Shield) */}
            <div
              onClick={() =>
                setPosConfig((prev) => ({ ...prev, statsVisibility: "actions_only" }))
              }
              className={`p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                posConfig.statsVisibility === "actions_only"
                  ? "border-indigo-600 bg-indigo-50/40 shadow-sm"
                  : "border-slate-200 hover:border-slate-300 bg-white"
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center">
                    <ShieldCheck className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="text-sm font-bold text-slate-800">
                      Shortcuts Only (Hide Sales Data)
                    </span>
                    <span className="ml-2 text-[9px] bg-indigo-100 text-indigo-700 font-extrabold px-1.5 py-0.5 rounded">
                      RECOMMENDED
                    </span>
                  </div>
                </div>
                {posConfig.statsVisibility === "actions_only" && (
                  <Check className="w-4 h-4 text-indigo-600" />
                )}
              </div>
              <p className="text-xs text-slate-500 mt-2 pl-9">
                Completely removes revenue & orders data to safeguard numbers from customers, while keeping Cash Drawer, Reservations, and Attendance active.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Advanced Privacy & Role Controls */}
      {posConfig.showQuickStatsStrip && posConfig.statsVisibility === "full" && (
        <div className="pt-2 border-t border-slate-100 space-y-4">
          <label className="text-xs font-black uppercase tracking-wider text-slate-400 block">
            Countertop Privacy & Staff Restrictions
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Control 1: Mask Sales by Default */}
            <div className="bg-slate-50/70 border border-slate-200/50 rounded-2xl p-4 flex items-center justify-between gap-3">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <EyeOff className="w-4 h-4 text-amber-600" />
                  <span className="text-xs font-bold text-slate-800">
                    Mask Sales Numbers by Default
                  </span>
                </div>
                <p className="text-[11px] text-slate-500">
                  Loads revenue as <span className="font-mono font-bold text-slate-700">₹••••••</span>. Tap the Eye icon on POS to reveal whenever needed.
                </p>
              </div>
              <Toggle
                id="toggle-mask-default"
                enabled={posConfig.maskSalesByDefault}
                onChange={(val) =>
                  setPosConfig((prev) => ({ ...prev, maskSalesByDefault: val }))
                }
              />
            </div>

            {/* Control 2: Restrict Stats to Manager */}
            <div className="bg-slate-50/70 border border-slate-200/50 rounded-2xl p-4 flex items-center justify-between gap-3">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-purple-600" />
                  <span className="text-xs font-bold text-slate-800">
                    Restrict Sales to Managers Only
                  </span>
                </div>
                <p className="text-[11px] text-slate-500">
                  Cashiers and waitstaff will never see daily sales totals. Only managers and owners have access to revenue data.
                </p>
              </div>
              <Toggle
                id="toggle-restrict-manager"
                enabled={posConfig.restrictStatsToManager}
                onChange={(val) =>
                  setPosConfig((prev) => ({ ...prev, restrictStatsToManager: val }))
                }
              />
            </div>

            {/* Control 3: Auto-Privacy Inactivity Timeout */}
            <div className="bg-slate-50/70 border border-slate-200/50 rounded-2xl p-4 flex items-center justify-between gap-3 md:col-span-2">
              <div className="space-y-0.5 max-w-md">
                <div className="flex items-center gap-2">
                  <Timer className="w-4 h-4 text-teal-600" />
                  <span className="text-xs font-bold text-slate-800">
                    Auto-Privacy Inactivity Timer
                  </span>
                </div>
                <p className="text-[11px] text-slate-500">
                  If sales figures are unmasked at the counter, automatically re-mask them after this period of inactivity so numbers are never left exposed.
                </p>
              </div>
              <select
                value={posConfig.autoMaskTimeout}
                onChange={(e) =>
                  setPosConfig((prev) => ({
                    ...prev,
                    autoMaskTimeout: Number(e.target.value),
                  }))
                }
                className="bg-white border border-slate-300 text-slate-800 font-bold text-xs rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-teal-500 shadow-sm"
              >
                <option value={30}>30 Seconds</option>
                <option value={45}>45 Seconds (Recommended)</option>
                <option value={60}>60 Seconds (1 Minute)</option>
                <option value={120}>120 Seconds (2 Minutes)</option>
                <option value={0}>Disabled (Manual Only)</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Info Tip with Keyboard Shortcut */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="flex items-start gap-2.5 p-3.5 bg-amber-50/60 border border-amber-200/60 rounded-xl text-amber-800 text-xs">
          <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <p>
            <strong className="font-semibold">Countertop Tip:</strong> Tap the <strong>Eye icon (👁️)</strong> directly on the black bar to mask or unmask today's sales in half a second.
          </p>
        </div>
        <div className="flex items-start gap-2.5 p-3.5 bg-indigo-50/60 border border-indigo-200/60 rounded-xl text-indigo-800 text-xs">
          <Keyboard className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />
          <p>
            <strong className="font-semibold">Instant Pro Shortcut:</strong> Press <kbd className="px-1.5 py-0.5 bg-white border border-indigo-200 rounded font-mono font-bold text-slate-800 shadow-xs">Alt + P</kbd> anywhere on POS to instantly toggle privacy mode.
          </p>
        </div>
      </div>
    </div>
  );
}
